import { History } from "history";
import { observer } from "mobx-react";
import React from "react";
import { FormattedMessage } from "react-intl";

import {
  BlockModel,
  EmbedBlockModel,
  LinkBlockModel,
  ReferenceBlockModel,
  RichtextBlockModel,
  YoutubeBlockModel,
} from "@courses-models/course";
import { easeInOutCubic } from "@use/easings";
import { HistoryContext } from "@use/react-router";
import { hrtime } from "@use/universal-hrtime";

import { colors, easings, fonts, routes } from "@apps/viewer/src/ts/config";
import { injectStore, Store } from "@apps/viewer/src/ts/store";

import { config } from "@apps/viewer/src/ts/components/layout/Header/config";
import { InitialSlide } from "@apps/viewer/src/ts/components/layout/Header/InitialSlide";
import { isAndroid, isIOS } from "@apps/viewer/src/ts/utils";

import { EmbedPreview } from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/EmbedPreview";
import { LinkPreview } from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/LinkPreview";
import { ReferencePreview } from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/ReferencePreview";
import { RichtextPreview } from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/RichtextPreview";
import { YoutubePreview } from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/YoutubePreview";

/**
 * Specifies the width in pixels of a single number in the progressbar.
 *
 * NB: This is strictly tied to the progressbar number's font-size, letter-spacing
 * etc.
 */
const NUMBER_WIDTH = 9.39;

/**
 * Specifies the desired size (diameter) of the bullets when fully expanded (ie.
 * on hover or when active).
 */
const BULLET_SIZE = 14;

/**
 * Specifies the minimum margin required between each of the bullets within the
 * progressbar.
 */
const MIN_BULLET_MARGIN = 25;

/**
 * Specifies the duration of the transition used to scroll selected bullets into
 * view automatically.
 */
const SCROLL_TRANSITION_DURATION = 360;

/**
 * Specifies the minimum amount of pixels the user has to move his/hers finger
 * in order to trigger a swipe navigation event.
 */
const SWIPE_THRESHOLD = 50;

type ProgressbarProps = {
  /**
   * A reference to the application lifecycle store, so we can update state
   * when needed.
   */
  store: Store;

  /**
   * Specifies if the header should currently be visible (ie. if the
   * application is ready to be rendered).
   */
  visible?: boolean;
};

type ProgressbarState = {
  /**
   * If set to the id of a bullet, then the preview associated with that
   * bullet will be open.
   */
  currentPreview?: string;
};

/**
 * The progressbar is positioned at the bottom of the screen,
 */
@observer
class ProgressbarUnwrapped extends React.Component<
  ProgressbarProps & { history?: History },
  ProgressbarState
> {
  /**
   * Prepare state to be available, even on initial render
   */
  public readonly state: ProgressbarState = {};

  /**
   * Will contain a reference to the <nav>-node that wraps the bullets in the
   * progressbar, so we can perform auto-centering when user navigates around
   * the course.
   */
  protected readonly bulletsRef: React.RefObject<HTMLDivElement | null> =
    React.createRef();

  /**
   * Will contain a reference to the bullet that is currently being hovered
   */
  protected readonly hoveredBulletRef: React.RefObject<HTMLLIElement | null> =
    React.createRef();

  /**
   * Used to cache the id of the currently selected block, so we know when the
   * user changes block (and thereby when to perform auto-centering of the
   * bullets in the progressbar).
   */
  protected currentBlockId: string;

  /**
   * Will contain a reference to the animation frame used to perform dynamic
   * scrolling to center currently selected bullet.
   */
  protected scrollAnimationFrame: number;

  /**
   * Will store the coordinates of the most recent touchstart event in order to
   * be able to properly recognize swipe directions.
   */
  protected initialTouch: { x?: number; y?: number };

  /**
   * timeout used to defer rendering of the previews when user hovers a bullet
   * slightly.
   */
  protected previewTimeout?: number;

  /**
   * Make sure that the initially selected bullet is being scrolled into view
   * whenever the application is opened.
   */
  public componentDidMount(): void {
    setTimeout(this.centerCurrentBullet);

    // Cache currently selected block id
    this.currentBlockId = this.props.store.blockId;

    window.addEventListener("keydown", this.onKeyDown);

    // The event listeners below are only required to be bound on touch devices
    if (!isIOS() && !isAndroid()) {
      return;
    }

    // Move code below out of the current execution context in order to ensure
    // that the mainContent DOM element has been rendered, before trying to
    // add our custom event listeners.
    setTimeout(() => {
      // We only want swipe navigation to occur on the main content, and not
      // on the header or progressbar.
      const mainContent = document.querySelector("main");

      if (mainContent) {
        mainContent.addEventListener("touchstart", this.onTouchStart);
        mainContent.addEventListener("touchmove", this.onTouchMove);
        mainContent.addEventListener("touchend", this.onTouchEnd);
        mainContent.addEventListener("touchcancel", this.onTouchEnd);
      }
    }, 100);
  }

  /**
   * Make sure that the newly selected block is scrolled into view, whenever
   * the user navigates around the course.
   */
  public componentDidUpdate(): void {
    if (this.currentBlockId !== this.props.store.blockId) {
      this.centerCurrentBullet();
    }

    // Cache currently selected block id
    this.currentBlockId = this.props.store.blockId;

    if (this.state.currentPreview === this.currentBlockId) {
      this.hidePreview();
    }
  }

  /**
   * Unbind event listeners on unmount
   */
  public componentWillUnmount(): void {
    window.removeEventListener("keydown", this.onKeyDown);

    // The event listeners below are only required to be bound on touch devices
    if (!isIOS() && !isAndroid()) {
      return;
    }

    const mainContent = document.querySelector("main");

    if (mainContent) {
      mainContent.removeEventListener("touchstart", this.onTouchStart);
      mainContent.removeEventListener("touchmove", this.onTouchMove);
      mainContent.removeEventListener("touchend", this.onTouchEnd);
      mainContent.removeEventListener("touchcancel", this.onTouchEnd);
    }
  }

  /**
   * Primary render method.
   */
  public render(): React.JSX.Element | null {
    // Bail out if there is only a single page within this course (in that
    // case a progressbar isn't really needed...)
    if (this.props.store.course.size < 2) {
      return null; // tslint:disable-line no-null-keyword
    }

    // HACK: Forces MobX to properly observe blockId changes, which is required by the progress bar.
    this.props.store.blockId;
    this.props.store.isMobile;

    return (
      <InitialSlide reversed in={this.props.visible}>
        {(ref) => (
          <>
            <footer
              ref={ref}
              className={`${this.props.store.isMobile ? "mobile" : ""}`}
            >
              <div
                className={`bullet-wrapper ${
                  this.props.store.isMobile ? "mobile" : ""
                }`}
              >
                {this.renderBullets()}
              </div>
              {this.renderButtons()}
            </footer>

            <style jsx>{`
              footer {
                /**
                         * Position the progressbar at the bottom of everything.
                         */
                position: fixed;
                bottom: 0;
                left: 0;
                right: 0;
                z-index: 100;

                /** Apply sizing to the progressbar */
                box-sizing: border-box;
                height: 51px;

                /** Apply internal horizontal padding */
                padding: 0 15px 0 30px;

                /** Use flexbox to render items inside */
                display: flex;
                flex-direction: row;
                align-items: center;

                /** Apply box styling */
                background: ${config.backgroundColor};
                box-shadow: 0 0 50px ${config.dropShadowColor};

                &.mobile {
                  /** Increase dimensions on mobile devices */
                  height: 80px;

                  /**
                             * Add padding that allows positioning the bullets
                             * next to buttons
                             */
                  padding: 0 86px;
                }
              }

              .bullet-wrapper {
                width: 100%;
                position: relative;

                /**
                         * Render gradients to the left and right of the bullets
                         * which will only become visible once bullets overflow
                         * in either direction in order to indicate that
                         * bullet ul can be scrolled on mobile
                         */
                &::before,
                &::after {
                  /** Force rendering */
                  content: "";

                  /** Setup generic positioning */
                  position: absolute;
                  top: 4px;
                  z-index: 2;

                  /** Setup size of gradient */
                  width: 21px;
                  height: 35px;

                  /** Bullets should still be selectable below gradients */
                  pointer-events: none;
                }

                /**
                         * Slightly increase bullet gradient on mobile to ensure
                         * bullets will be covered
                         */
                &.mobile::before,
                &.mobile::after {
                  height: 51px;
                }

                /** Render gradient to the left */
                &::before {
                  left: -1px;
                  background-image: linear-gradient(
                    to right,
                    rgba(255, 255, 255, 0.96),
                    rgba(255, 255, 255, 0)
                  );
                }

                /** Render gradient to the right */
                &::after {
                  right: -1px;
                  background-image: linear-gradient(
                    to left,
                    rgba(255, 255, 255, 0.96),
                    rgba(255, 255, 255, 0)
                  );
                }
              }
            `}</style>
          </>
        )}
      </InitialSlide>
    );
  }

  /**
   * Helper that'll render the list of bullets, each representing a single
   * page available within this course.
   */
  // tslint:disable-next-line max-func-body-length
  protected renderBullets(): React.JSX.Element {
    const { blockId, course, isMobile } = this.props.store;

    return (
      <nav ref={this.bulletsRef} className={`${isMobile ? "mobile" : ""}`}>
        <ul className={`${isMobile ? "mobile" : ""}`}>
          {course.map((item, index) => [
            this.renderBullet(item, item.id === blockId, index),
            <li className="separator" key={`separator-${item.id}`} />,
          ])}
        </ul>

        <style jsx>{`
          nav {
            /** Fill up the container */
            flex-grow: 1;

            /** Allow user to scroll between overflowing bullets */
            overflow-x: auto;

            &.mobile {
              /** Move bullets further up on mobile devices */
              align-self: flex-start;
              margin-top: 4px;
            }
          }

          ul {
            /** Disable default list styling */
            list-style: none;
            margin: 0;
            padding: 0 20px;

            /**
                         * Use flexbox inside to evenly distribute the bullets
                         * inside.
                         */
            display: flex;

            &.mobile {
              /** Position scrollbar on the bottom of the ProgressBar */
              padding: 0 20px 27px;
            }
          }

          /** Use after element to render separator */
          li.separator {
            /** Render single pixel line */
            height: 1px;
            margin: 25px 4px;

            /** Render the element */
            flex: 1;
            background: #ccc;

            /**
                         * Enforce a minimum width on separators on mobile
                         * devices
                         */
            min-width: 20px;

            /** Hide the final separator */
            &:last-child {
              display: none;
            }
          }
        `}</style>
      </nav>
    );
  }

  /**
   * Helper that'll render a single bullet which represents a single content
   * page.
   */
  protected renderBullet(
    item: BlockModel,
    isActive: boolean,
    index: number,
  ): React.JSX.Element {
    const previewOpen =
      this.state.currentPreview === item.id &&
      !isActive &&
      !this.props.store.isMobile;
    const bulletRef = previewOpen ? this.hoveredBulletRef : undefined;

    const onClick = (evt: React.MouseEvent<HTMLLIElement>) => {
      this.onBulletClick(evt);
      this.hidePreview();
    };

    return (
      <li
        className={`bullet-wrapper ${isActive ? "active" : ""}`}
        key={item.id}
        data-id={item.id}
        role="button"
        onClick={onClick}
        onMouseEnter={this.displayPreview}
        onMouseLeave={this.hidePreview}
      >
        <span className="bullet" ref={bulletRef}>
          {this.getPreview(item, previewOpen, index)}
        </span>

        <style jsx>{`
          li.bullet-wrapper {
            /** Fixate the size of the item */
            height: 51px;
            flex-basis: ${BULLET_SIZE + 8 * 2}px;
            flex-shrink: 0;
            flex-grow: 0;

            /**
                         * Apply negative margins to accomodate for the fact
                         * that we've made the node larger than the actual size
                         * of the bullet to expand clickable area.
                         */
            margin: 0 -8px;

            /** Allow position: absolute to render bullet within */
            position: relative;

            /** Indicate that the element is clickable */
            cursor: pointer;

            /** Ensure preview is shown on top of other content */
            z-index: 1;

            /** Position bullet */
            & .bullet {
              /** Center inside the container */
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);

              /** Set up sizing */
              display: block;
              width: ${BULLET_SIZE}px;
              height: ${BULLET_SIZE}px;

              /** Render bullet using pseudo element */
              &::before {
                /** Setup sizing */
                box-sizing: border-box;
                display: block;
                width: 100%;
                height: 100%;

                /** Slightly shrink non-interacted bullets */
                transform: scale(0.714);

                /** Apply styling */
                background: ${colors.darkGrey};
                border: 2px solid ${colors.darkGrey};
                border-radius: 50%;

                /** Prepare hover / active transitions */
                transition: transform 0.1s ${easings.inOutCubic},
                  background-color 0.1s linear, border-color 0.1s linear;

                /** Force rendering of bullet */
                content: "";
              }
            }

            /** Apply hover styling */
            &:hover .bullet::before {
              background-color: transparent;
              border-color: ${colors.orange};

              /** Perform scale-up */
              transform: scale(1);
            }

            /** Apply active styling */
            &.active {
              /** Active bullets are no longer clickable */
              cursor: default;

              /** Update styling */
              & .bullet::before {
                background-color: ${colors.orange} !important;
                border-color: ${colors.orange};

                /** Perform scale-up */
                transform: scale(1);
              }
            }
          }
        `}</style>
      </li>
    );
  }

  /**
   * Helper that'll determine what type of Preview should be used for the given
   * block.
   */
  protected getPreview(
    block: BlockModel,
    open: boolean,
    index: number,
  ): React.JSX.Element | undefined {
    if (block instanceof EmbedBlockModel) {
      return (
        <EmbedPreview
          active={open}
          targetRef={this.hoveredBulletRef}
          steps={{
            current: index + 1,
            total: this.props.store.course.size,
          }}
          model={block}
        />
      );
    } else if (block instanceof LinkBlockModel) {
      return (
        <LinkPreview
          active={open}
          targetRef={this.hoveredBulletRef}
          steps={{
            current: index + 1,
            total: this.props.store.course.size,
          }}
          model={block}
        />
      );
    } else if (block instanceof ReferenceBlockModel) {
      return (
        <ReferencePreview
          active={open}
          targetRef={this.hoveredBulletRef}
          steps={{
            current: index + 1,
            total: this.props.store.course.size,
          }}
          model={block}
          store={this.props.store}
        />
      );
    } else if (block instanceof YoutubeBlockModel) {
      return (
        <YoutubePreview
          active={open}
          targetRef={this.hoveredBulletRef}
          steps={{
            current: index + 1,
            total: this.props.store.course.size,
          }}
          model={block}
        />
      );
    } else if (block instanceof RichtextBlockModel) {
      return (
        <RichtextPreview
          active={open}
          targetRef={this.hoveredBulletRef}
          steps={{
            current: index + 1,
            total: this.props.store.course.size,
          }}
          model={block}
        />
      );
    }

    return undefined;
  }

  /**
   * Listener that, when called, will displayed the preview related to the
   * bullet that was hovered
   */
  protected displayPreview = (evt: React.MouseEvent) => {
    // iPads incorrectly recognizes mouseenter, hence we must bail out if an
    // iOS based device gets here, since previews should be disabled on mobile
    // devices.
    if (isIOS() || isAndroid()) {
      return;
    }

    const target = evt.currentTarget as EventTarget & HTMLElement;

    window.clearTimeout(this.previewTimeout);
    this.previewTimeout = window.setTimeout(() => {
      this.setState({ currentPreview: target.dataset.id });
    }, 100);
  };

  /**
   * Listener that, when called, will hide any currently open preview
   */
  protected hidePreview = () => {
    window.clearTimeout(this.previewTimeout);
    this.setState({ currentPreview: undefined });
  };

  /**
   * Renderer that'll inject the buttons to navigate between blocks within the
   * course.
   */
  // tslint:disable-next-line max-func-body-length
  protected renderButtons(): React.JSX.Element {
    const { blockId, course, isMobile } = this.props.store;
    const currentIndex = course.getIndexById(blockId);

    // To get the minimum size that fits the longest step (in pxs), we compute
    // the magnitude of the amount of courses (to get the maximum amount of
    // chars) and multiply that with the width of a single char
    const currentStepMinWidth =
      NUMBER_WIDTH * (Math.floor(Math.log(course.size) / Math.LN10 + 0.01) + 1);

    return (
      <React.Fragment>
        <button
          disabled={currentIndex === 0}
          className={`prev ${isMobile ? "mobile" : ""}`}
          onClick={this.onPrevClick}
        />

        <span className={`progress ${isMobile ? "mobile" : ""}`}>
          <span className="desktop">
            <span
              className="currentStep"
              style={{ minWidth: currentStepMinWidth }}
            >
              <FormattedMessage
                id="Progessbar__stepX__desktop"
                defaultMessage="{ x }"
                values={{
                  x: currentIndex + 1,
                }}
              />
            </span>{" "}
            <FormattedMessage
              id="Progressbar__ofY__desktop"
              defaultMessage="af { y }"
              values={{
                y: course.size,
              }}
            />
          </span>

          <span className="mobile">
            <FormattedMessage
              id="Progressbar__stepX__mobile"
              defaultMessage="Trin { x }"
              values={{
                x: currentIndex + 1,
              }}
            />{" "}
            <FormattedMessage
              id="Progressbar__ofY__mobile"
              defaultMessage="af { y }"
              values={{
                y: course.size,
              }}
            />
          </span>
        </span>

        <button
          disabled={currentIndex === course.size - 1}
          className={`next ${isMobile ? "mobile" : ""}`}
          onClick={this.onNextClick}
        />

        <style jsx>{`
          .currentStep {
            display: inline-block;
            text-align: right;
          }

          button {
            /** Allow position: absolute to render lines within */
            position: relative;

            /** Reset default styling */
            -webkit-appearance: none;
            border: none;
            outline: none;

            /** Set up sizing */
            flex-grow: 0;
            flex-shrink: 0;
            flex-basis: 35px;
            height: 35px;

            /** Apply styling */
            background: #e3e3e3;
            border-radius: 50%;

            /** Indicate that the element is clickable */
            cursor: pointer;

            /** Prepare hover transition */
            transition: background-color 0.1s linear, opacity 0.1s linear;

            /**
                         * Buttons will be positioned differently on mobile,
                         * and as per spec should be larger
                         */
            &.mobile {
              width: 53px;
              height: 53px;

              /** Increase chevron size as well to accomodate for larger buttons */
              &::before,
              &::after {
                width: 5px;
                height: 18px;
              }
            }

            &[disabled] {
              cursor: not-allowed;
              opacity: 0.5;
            }

            /** Render chevron using pseudo elements */
            &::before,
            &::after {
              /** Position the line */
              position: absolute;
              top: 50%;
              left: 60%;

              /** Set up sizing */
              display: block;
              width: 3px;
              height: 12px;

              /** Render the line using solid background */
              background: ${colors.darkGrey};
              content: "";

              /** Rotate the line slightly */
              transform: rotate(45deg);
              transform-origin: top right;

              /** Prepare hover transition */
              transition: background-color 0.1s linear;
            }

            &::after {
              /** Render upwards instead of down */
              top: auto;
              bottom: 50%;

              /** Rotate the other direction */
              transform: rotate(-45deg);
              transform-origin: bottom right;
            }

            /** Apply hover transition */
            &:not([disabled]):hover {
              background-color: ${colors.orange};

              &::before,
              &::after {
                background-color: ${colors.white};
              }
            }
          }

          button.prev {
            /** Apply margin between bullets and buttons */
            margin-left: 30px;

            /** Inverse chevron */
            &::before,
            &::after {
              /** Offset from right rather than left */
              left: auto;
              right: 60%;

              /** Inverse line rotation */
              transform: rotate(-45deg);
              transform-origin: top left;
            }

            &::after {
              transform: rotate(45deg);
              transform-origin: bottom left;
            }

            &.mobile {
              /**
                             * Position the bottom to the left of the progress
                             * bar on mobile
                             */
              position: absolute;
              left: 0;
              top: 50%;
              transform: translateY(-50%);
              margin-left: 20px;
            }
          }

          button.next.mobile {
            /**
                         * Position the bottom to the right of the progress
                         * bar on mobile
                         */
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            margin-right: 20px;
          }

          .progress {
            /** Prevent growing / shrinking */
            flex-grow: 0;
            flex-shrink: 0;
            flex-basis: max-content;
            width: max-content;

            /** Apply horizontal margin */
            margin: 0 15px;

            /** Apply font styling */
            font-size: 16px;
            font-family: ${fonts.franklin};
            color: ${colors.black};

            /** Apply styling to highlighted segments */
            & > span > :global(:first-child) {
              color: ${colors.orange};
            }

            /** Alter position of progress on mobile devices */
            &.mobile {
              /** Use a smaller font-size on mobile */
              font-size: 13px;

              /** And center the progress text as well */
              position: absolute;
              left: 50%;
              bottom: 18px;
              transform: translateX(-50%);
              margin: 0;
            }

            /** Hide mobile progress message by default */
            & .mobile {
              display: none;
            }

            /** Show mobile progress on small displays */
            &.mobile {
              & .desktop {
                display: none;
              }

              & .mobile {
                display: inline;
              }
            }
          }
        `}</style>
      </React.Fragment>
    );
  }

  /**
   * Listener that's triggered when the user clicks a bullet, making sure that
   * he'll be brought to that page within the course.
   */
  protected onBulletClick = (evt: React.MouseEvent<HTMLLIElement>) => {
    const { history, store } = this.props;
    const { course } = store;

    // Throw an error if attempting to navigate without access to the
    // history manager
    if (!history) {
      throw new Error(
        "<Progressbar />.onBulletClick: Lost reference to <Router /> context.",
      );
    }

    // Extract id of the clicked bullet
    const blockId = evt.currentTarget.getAttribute("data-id");

    // Bail out if unable to extract block id
    if (blockId === null) {
      throw new Error(
        "<Progressbar />.onBulletClick: Unable to extract block id from clicked bullet.",
      );
    }

    // If we're navigating to the first item, then simply go to the root of
    // the site
    if (course.getIndexById(blockId) === 0) {
      history.push("/");

      // Abort further execution, we've already navigated!
      return;
    }

    // ... Otherwise go straight to the clicked block
    history.push(routes.block.replace(":blockId", blockId));
  };

  /**
   * Listener that's triggered when the user clicks the previous button,
   * taking him straight to the previous block.
   */
  protected onPrevClick = () => {
    const { history, store } = this.props;
    const { blockId, course } = store;

    // Throw an error if attempting to navigate without access to the
    // history manager
    if (!history) {
      throw new Error(
        "<Progressbar />.onPrevClick: Lost reference to <Router /> context.",
      );
    }

    // Find index of the currently active tab
    const currentIndex = course.getIndexById(blockId);

    // If we're navigating to the first item, then simply go to the root of
    // the site
    if (currentIndex === 1) {
      history.push("/");

      // Abort further execution, we've already navigated!
      return;
    }

    // Get id of the previous block in the course
    const prevId = course.getIdByIndex(currentIndex - 1);

    // Bail out if no previous block exists
    if (currentIndex === -1 || prevId === undefined) {
      return;
    }

    // ... Otherwise go straight to the previous block
    history.push(routes.block.replace(":blockId", String(prevId)));
  };

  /**
   * Listener that's triggered when the user clicks the next button, taking
   * him straight to the next block.
   */
  protected onNextClick = () => {
    const { history, store } = this.props;
    const { blockId, course } = store;

    // Throw an error if attempting to navigate without access to the
    // history manager
    if (!history) {
      throw new Error(
        "<Progressbar />.onNextClick: Lost reference to <Router /> context.",
      );
    }

    // Find index of the currently active tab
    const currentIndex = course.getIndexById(blockId);

    // Get id of the next block in the course
    const nextId = course.getIdByIndex(currentIndex + 1);

    // Bail out if no next block exists
    if (currentIndex === -1 || nextId === undefined) {
      return;
    }

    // ... Otherwise go straight to the next block
    history.push(routes.block.replace(":blockId", String(nextId)));
  };

  /**
   * Helper that'll calculate the desired offset to scroll the current bullet
   * to the middle of the progressbar as the user navigates around the
   * course.
   */
  protected centerCurrentBullet = () => {
    const { blockId, course } = this.props.store;
    const wrapper = this.bulletsRef.current;

    // Bail out if the progressbar is no longer mounted
    if (!wrapper) {
      return;
    }

    // Stop current scroll transition
    cancelAnimationFrame(this.scrollAnimationFrame);

    // Cache size of the progressbar
    const internalPadding = 40;
    const nodeWidth = wrapper.offsetWidth - internalPadding;
    const maxScrollOffset = wrapper.scrollWidth - nodeWidth;

    // Calculate offset of the bullet
    const currentIndex = course.getIndexById(blockId);
    const bulletOffset = currentIndex * (BULLET_SIZE + MIN_BULLET_MARGIN);

    // Calculate scroll offset needed to center this bullet visually (the
    // 7px is added to center around the bullet, which is 14px wide)
    const desiredScrollOffset = bulletOffset - Math.round(nodeWidth / 2) + 7;

    // Ensure that we're going to scroll to an offset that's within the
    // possible bounds
    const scrollTo = Math.max(
      0,
      Math.min(maxScrollOffset, desiredScrollOffset),
    );

    // Prepare scroll transition
    const startedAt = hrtime();
    const scrollFrom = wrapper.scrollLeft;
    const scroll = scrollTo - scrollFrom;

    // Perform scrolling
    const animatedScrolling = (tstamp: number) => {
      // Calculate eased progress of the transition
      const p = easeInOutCubic(
        Math.max(
          0,
          Math.min(1, (tstamp - startedAt) / SCROLL_TRANSITION_DURATION),
        ),
      );

      // Perform scrolling!
      wrapper.scrollLeft = Math.round(scrollFrom + scroll * p);

      // Keep on scrolling until the transition has ended!
      if (p < 1) {
        this.scrollAnimationFrame = requestAnimationFrame(animatedScrolling);
      }
    };

    animatedScrolling(startedAt);
  };

  /**
   * Listener that'll allow navigating back and forth using keyboard shortcuts
   */
  protected onKeyDown = (evt: KeyboardEvent) => {
    const { store } = this.props;

    // In case any kind of overlay is active, then default navigation should
    // be disabled.
    if (store.displayProgressionPrompt || !!this.props.store.lightboxContent) {
      return;
    }

    switch (evt.key) {
      case "ArrowLeft":
        this.onPrevClick();
        break;

      case "ArrowRight":
        this.onNextClick();
        break;

      default:
      // No other keyPresses should be handled
    }
  };

  /**
   * Internal helper to be called on touch start to determine where the touch
   * begun.
   */
  protected onTouchStart = (evt: TouchEvent) => {
    if (this.props.store.isDraggingSlider) {
      return;
    }

    this.initialTouch = {
      x: evt.touches[0].clientX,
      y: evt.touches[0].clientY,
    };
  };

  /**
   * Internal helper to be called on touch move to detect swipe direction and
   * navigate to the proper page.
   */
  protected onTouchMove = (evt: TouchEvent) => {
    // Bail out if initial point hasn't been specified yet.
    if (
      !this.initialTouch.x ||
      !this.initialTouch.y ||
      this.props.store.isDraggingSlider
    ) {
      return;
    }

    const endX = evt.touches[0].clientX;
    const endY = evt.touches[0].clientY;

    const xDiff = this.initialTouch.x - endX;
    const yDiff = this.initialTouch.y - endY;

    // Ensure this is indeed a horizontal swipe, and not a vertical swipe, and
    // that the threshold has been crossed
    if (
      Math.abs(xDiff) > Math.abs(yDiff) &&
      Math.abs(xDiff) > SWIPE_THRESHOLD
    ) {
      if (xDiff > 0) {
        // Left swipe
        this.onNextClick();
      } else {
        // Right swipe
        this.onPrevClick();
      }

      // Reset values on successful navigation
      this.onTouchEnd();
    }
  };

  /**
   * Once a touch event ends, ensure that initialTouch values are cleared
   */
  protected onTouchEnd = () => {
    this.initialTouch = {};
  };
}

/**
 * Helper that'll inject a reference to the history manager used within the
 * application lifecycle.
 */
// tslint:disable-next-line variable-name
const ProgressbarHistoryInjector: React.FC<ProgressbarProps> = (props) => (
  <HistoryContext.Consumer>
    {(history) => <ProgressbarUnwrapped history={history} {...props} />}
  </HistoryContext.Consumer>
);

// tslint:disable-next-line variable-name
export const Progressbar = injectStore(ProgressbarHistoryInjector);
